import { Box, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  NumberInput,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  minValue,
  number,
  required,
  useNotify,
  useRecordContext,
} from "react-admin";
import {
  TSupplier,
  TTenantPricing,
  TTenantPricingUpsert,
  createTenantPricing,
  getTenantPricing,
  updateTenantPricing,
} from "@/api";
import { country } from "@/config/country";

type PricingToolbarProps = {
  isSaving: boolean;
};

const PricingToolbar = ({ isSaving }: PricingToolbarProps) => (
  <Toolbar>
    <SaveButton disabled={isSaving} />
  </Toolbar>
);

export const SupplierMarketingPricingTab = () => {
  const supplier = useRecordContext<TSupplier>();
  const notify = useNotify();
  const [pricing, setPricing] = useState<TTenantPricing | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPricing = useCallback(async () => {
    if (!supplier?.id) return;
    setLoading(true);
    try {
      const data = await getTenantPricing(supplier.id);
      setPricing(data ?? null);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        notify(error?.message || "No se pudo cargar el pricing.", {
          type: "warning",
        });
      }
      setPricing(null);
    } finally {
      setLoading(false);
    }
  }, [supplier?.id, notify]);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const defaultValues = useMemo(
    () => ({
      country: pricing?.country || country.code?.toUpperCase() || "",
      costPerDelivery: pricing?.costPerDelivery ?? 0,
      costPerResponse: pricing?.costPerResponse ?? 0,
    }),
    [pricing]
  );

  const handleSubmit = async (values: any) => {
    if (!supplier?.id) return;
    setSaving(true);
    const payload: TTenantPricingUpsert = {
      tenantId: supplier.id,
      country: values.country?.toUpperCase(),
      costPerDelivery: Number(values.costPerDelivery),
      costPerResponse: Number(values.costPerResponse),
    };

    try {
      if (pricing?.id) {
        await updateTenantPricing(pricing.id, payload);
      } else {
        await createTenantPricing(payload);
      }
      notify("Pricing guardado correctamente.", { type: "success" });
      await loadPricing();
    } catch (error: any) {
      notify(error?.message || "Error al guardar el pricing.", {
        type: "warning",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!supplier) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6">Marketing</Typography>
      <Typography variant="body2" color="text.secondary">
        Define el costo por entrega y por respuesta para este supplier.
      </Typography>
      {loading ? (
        <Typography sx={{ mt: 2 }}>Cargando pricing...</Typography>
      ) : (
        <SimpleForm
          key={pricing?.id ?? "new"}
          onSubmit={handleSubmit}
          toolbar={<PricingToolbar isSaving={saving} />}
          defaultValues={defaultValues}
          warnWhenUnsavedChanges
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextInput
                source="country"
                label="País (ISO 2)"
                fullWidth
                inputProps={{ maxLength: 2 }}
                parse={(value: string) => value?.toUpperCase()}
                validate={[required()]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberInput
                source="costPerDelivery"
                label="Costo por entrega"
                fullWidth
                inputProps={{ step: 0.00001, min: 0 }}
                validate={[required(), number(), minValue(0)]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberInput
                source="costPerResponse"
                label="Costo por respuesta"
                fullWidth
                inputProps={{ step: 0.00001, min: 0 }}
                validate={[required(), number(), minValue(0)]}
              />
            </Grid>
          </Grid>
        </SimpleForm>
      )}
    </Box>
  );
};
